/**
 * LoginForm — autenticazione Phase 5 (IMPL-P5-010).
 *
 * UC-007 login form. `react-hook-form` + `zodResolver(loginSchema)` from
 * `@app/shared`. Submits to `/api/auth/custom/login` via
 * `authClient.login()`. On success refreshes the auth session (so protected
 * routes stop bouncing back to `/accedi`) and redirects either to the
 * `redirectTo` query-string param or to a role-aware default landing page.
 *
 * Server-error handling:
 *   - 401 → neutral Italian copy ("Email o password non corretti").
 *   - 429 → lockout copy + link to forgot-password.
 *   - Other → generic neutral copy.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@app/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { authClient, isAuthClientError, type Role } from '../lib/auth-client';
import { isLockedOut, serverErrorToItalian } from '../lib/errors';
import { useAuthSession } from '../hooks/useAuthSession';
import { PasswordField } from './PasswordField';

const ROLE_LANDING: Record<Role, string> = {
  CLIENT: '/pratiche',
  OPERATOR: '/lavorazione',
  ADMIN: '/admin',
};

interface ServerError {
  status: number;
  code: string;
}

export function LoginForm() {
  const [serverError, setServerError] = useState<ServerError | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuthSession();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const rememberMe = watch('rememberMe') ?? false;

  async function onSubmit(values: LoginInput) {
    setServerError(null);
    try {
      const res = await authClient.login(values);

      // Re-hydrate the AuthSessionProvider so /me reflects the new session
      // before the navigation runs and protected routes mount.
      await refresh();

      const redirectTo = searchParams.get('redirectTo');
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
        return;
      }
      navigate(ROLE_LANDING[res.user.role] ?? '/', { replace: true });
    } catch (err: unknown) {
      if (isAuthClientError(err)) {
        setServerError({ status: err.status, code: err.code });
      } else {
        setServerError({ status: 0, code: 'UNKNOWN' });
      }
    }
  }

  const isLockout = serverError ? isLockedOut(serverError.status, serverError.code) : false;

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      aria-describedby={serverError ? 'login-server-error' : undefined}
    >
      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="nome@esempio.it"
          aria-invalid={!!errors.email || undefined}
          aria-describedby={errors.email ? 'login-email-error' : undefined}
          {...register('email')}
        />
        {errors.email ? (
          <p id="login-email-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      {/* Password + forgot-password link */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="login-password">Password</Label>
          <Link
            to="/password-dimenticata"
            className="text-xs font-medium text-primary underline-offset-4 hover:underline"
          >
            Password dimenticata?
          </Link>
        </div>
        <PasswordField
          id="login-password"
          autoComplete="current-password"
          aria-describedby={errors.password ? 'login-password-error' : undefined}
          invalid={!!errors.password}
          {...register('password')}
        />
        {errors.password ? (
          <p id="login-password-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.password.message}
          </p>
        ) : null}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="login-remember"
          checked={rememberMe}
          onCheckedChange={(v) => setValue('rememberMe', v === true, { shouldDirty: true })}
        />
        <Label htmlFor="login-remember" className="text-sm font-normal">
          Ricordami su questo dispositivo
        </Label>
      </div>

      {/* Server errors */}
      {serverError ? (
        isLockout ? (
          <Alert variant="destructive" id="login-server-error">
            <AlertTitle>Troppi tentativi</AlertTitle>
            <AlertDescription>
              Troppi tentativi di accesso. Riprova tra alcuni minuti oppure{' '}
              <Link
                to="/password-dimenticata"
                className="underline underline-offset-4 font-medium"
              >
                reimposta la password
              </Link>
              .
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive" id="login-server-error">
            <AlertDescription>
              {serverErrorToItalian(
                serverError.status === 401 ? 'INVALID_CREDENTIALS' : serverError.code,
              )}
            </AlertDescription>
          </Alert>
        )
      ) : null}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Accesso in corso…' : 'Accedi'}
      </Button>
    </form>
  );
}
